/**
 * Document numbering, `{prefix}-{year}-{seq}` — e.g. `Q-2026-0106` for a
 * Standard Quote (DESIGN-SPEC.md §12.7), `PA-2026-0105` for a G702/G703 Pay
 * Application (CLAUDE-CODE-INSTRUCTIONS.md Rule 15). The sequence is the
 * highest one already used this year for that prefix, plus one. Still
 * editable — subs who run their own numbering can overtype it.
 */
const NUMBER_RE = (prefix: string, year: number) => new RegExp(`^${prefix}-${year}-(\\d+)$`, "i");

export function formatDocNumber(prefix: string, year: number, sequence: number): string {
  return `${prefix}-${year}-${String(sequence).padStart(4, "0")}`;
}

/** Highest sequence already issued this year for this prefix, plus one. */
export function nextDocNumber(
  prefix: string,
  existingDocIds: string[],
  year: number = new Date().getFullYear(),
): string {
  const re = NUMBER_RE(prefix, year);
  const highest = existingDocIds.reduce((max, id) => {
    const match = id.trim().match(re);
    return match ? Math.max(max, Number(match[1])) : max;
  }, 0);
  return formatDocNumber(prefix, year, highest + 1);
}

export function isNumberTaken(candidate: string, existingDocIds: string[]): boolean {
  const needle = candidate.trim().toUpperCase();
  if (!needle) return false;
  return existingDocIds.some((id) => id.trim().toUpperCase() === needle);
}

export function formatQuoteNumber(year: number, sequence: number): string {
  return formatDocNumber("Q", year, sequence);
}

export function nextQuoteNumber(existingDocIds: string[], year: number = new Date().getFullYear()): string {
  return nextDocNumber("Q", existingDocIds, year);
}

export function formatPaymentAppNumber(year: number, sequence: number): string {
  return formatDocNumber("PA", year, sequence);
}

export function nextPaymentAppNumber(existingDocIds: string[], year: number = new Date().getFullYear()): string {
  return nextDocNumber("PA", existingDocIds, year);
}

export function formatInvoiceNumber(year: number, sequence: number): string {
  return formatDocNumber("INV", year, sequence);
}

export function nextInvoiceNumber(existingDocIds: string[], year: number = new Date().getFullYear()): string {
  return nextDocNumber("INV", existingDocIds, year);
}
